import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { labelRol, colorRol } from "@/lib/constants";
import NotificationBell from "@/components/NotificationBell";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  LayoutDashboard,
  FolderKanban,
  Trello,
  Users,
  ListChecks,
  FileText,
  Shield,
  Footprints,
  LogOut,
  Menu,
  Inbox,
  PackageOpen,
  Bell,
  ChevronDown,
  PanelLeftClose,
  CalendarDays,
  Box,
  BookOpen,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  {
    to: "/colecciones", label: "Colecciones", icon: FolderKanban, testid: "nav-colecciones",
    children: [{ to: "/kanban", label: "Kanban", icon: Trello, testid: "nav-kanban" }],
  },
  { to: "/hormas", label: "Hormas", icon: Box, testid: "nav-hormas" },
];
const ADMIN_NAV = [
  { to: "/usuarios", label: "Usuarios", icon: Users, testid: "nav-usuarios" },
  { to: "/roles", label: "Roles y permisos", icon: Shield, testid: "nav-roles" },
  { to: "/listas", label: "Listas", icon: ListChecks, testid: "nav-listas" },
  { to: "/notificaciones", label: "Notificaciones", icon: Bell, testid: "nav-notificaciones" },
  { to: "/plantillas", label: "Plantillas", icon: FileText, testid: "nav-plantillas" },
  { to: "/calendario", label: "Calendario", icon: CalendarDays, testid: "nav-calendario" },
];

// Persiste el grupo abierto entre remontajes de Layout (cada ruta lo remonta vía <Protected>).
// Así el nuevo montaje arranca con el grupo previo abierto y el cierre automático anima igual que el manual.
let grpPersistido = null;
// Persiste el estado colapsado de la barra lateral entre remontajes de Layout.
let colapsadoPersistido = false;

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pendientes, setPendientes] = useState(0);
  const [openGrp, setOpenGrp] = useState(grpPersistido);
  const [collapsed, setCollapsed] = useState(colapsadoPersistido);

  useEffect(() => { colapsadoPersistido = collapsed; }, [collapsed]);

  // El grupo activo (Colecciones/Kanban o Muestras/Tablero) se expande automáticamente; solo uno a la vez.
  useEffect(() => {
    if (location.pathname.startsWith("/colecciones") || location.pathname.startsWith("/kanban")) setOpenGrp("col");
    else if (location.pathname.startsWith("/muestras")) setOpenGrp("mue");
  }, [location.pathname]);

  // Recuerda el grupo abierto para que sobreviva al remontaje y la transición de cierre sea suave.
  useEffect(() => { grpPersistido = openGrp; }, [openGrp]);

  // Cierra el drawer al cambiar de ruta.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Conteo de solicitudes pendientes (solo para quienes autorizan). Refresca al navegar y ante cambios.
  useEffect(() => {
    let activo = true;
    const fetchPend = () => {
      import("@/lib/api").then(({ default: api }) => {
        api.get("/solicitudes/resumen").then((r) => { if (activo) setPendientes(r.data.pendientes || 0); }).catch(() => {});
      });
    };
    fetchPend();
    window.addEventListener("solicitudes-changed", fetchPend);
    return () => { activo = false; window.removeEventListener("solicitudes-changed", fetchPend); };
  }, [location.pathname]);

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const NavLink = ({ item }) => {
    const Icon = item.icon;
    const active = isActive(item.to);
    return (
      <Link
        to={item.to}
        data-testid={item.testid}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
        }`}
      >
        <Icon size={18} strokeWidth={2} />
        <span className="flex-1">{item.label}</span>
        {item.badge > 0 && (
          <span className="min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center" data-testid="solicitudes-badge">
            {item.badge}
          </span>
        )}
      </Link>
    );
  };

  const NavGroup = ({ item, gkey, children }) => {
    const Icon = item.icon;
    const active = isActive(item.to);
    const open = openGrp === gkey;
    return (
      <div>
        <div className={`flex items-center rounded-lg ${active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          <Link to={item.to} data-testid={item.testid} className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium flex-1 min-w-0">
            <Icon size={18} strokeWidth={2} />
            <span className="flex-1 truncate">{item.label}</span>
          </Link>
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpenGrp(open ? null : gkey); }}
            data-testid={`${item.testid}-toggle`}
            aria-label={open ? "Colapsar" : "Expandir"}
            className={`px-2.5 py-2.5 ${active ? "text-white/80 hover:text-white" : "text-slate-400 hover:text-slate-700"}`}
          >
            <ChevronDown size={16} className={`transition-transform ${open ? "" : "-rotate-90"}`} />
          </button>
        </div>
        <div
          className={`grid transition-[grid-template-rows] duration-200 ease-in-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
        >
          <div className="overflow-hidden min-h-0">
            <div className="mt-1 space-y-1">{children}</div>
          </div>
        </div>
      </div>
    );
  };

  const SubLink = ({ item }) => {
    const Icon = item.icon;
    const active = isActive(item.to);
    return (
      <Link
        to={item.to}
        data-testid={item.testid}
        className={`flex items-center gap-2.5 pl-11 pr-3 py-2 rounded-lg text-sm transition-colors ${
          active ? "bg-slate-100 text-slate-900 font-semibold" : "text-slate-500 hover:bg-slate-100"
        }`}
      >
        <Icon size={15} strokeWidth={2} />
        {item.label}
      </Link>
    );
  };

  // Todos ven Solicitudes (las de muestra en solo lectura); crear/autorizar dependen de su permiso.
  const solicitudesVisible = !!user;
  const muestrasVisible = user?.es_admin || user?.perms?.acciones?.ver_muestras || user?.perms?.acciones?.gestionar_muestras;

  // Contenido reutilizable de la barra lateral (usado en sidebar fijo y en drawer).
  const SidebarInner = ({ showBell }) => (
    <>
      <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-200 shrink-0">
        <div className="h-9 w-9 rounded-xl bg-blue-600 flex items-center justify-center text-white">
          <Footprints size={20} />
        </div>
        <span className="font-head font-extrabold tracking-tight">PLM</span>
        {showBell && (
          <div className="ml-auto flex items-center gap-1">
            <NotificationBell />
            <button onClick={() => setCollapsed(true)} data-testid="sidebar-collapse-btn" aria-label="Colapsar menú" className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
              <PanelLeftClose size={18} />
            </button>
          </div>
        )}
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {NAV.map((i) => i.children ? (
          <NavGroup key={i.to} item={i} gkey="col">
            {i.children.map((c) => <SubLink key={c.to} item={c} />)}
          </NavGroup>
        ) : (
          <NavLink key={i.to} item={i} />
        ))}
        {solicitudesVisible && (
          <NavLink item={{ to: "/solicitudes", label: "Solicitudes", icon: Inbox, testid: "nav-solicitudes", badge: pendientes }} />
        )}
        {muestrasVisible && (
          <NavGroup item={{ to: "/muestras", label: "Muestras", icon: PackageOpen, testid: "nav-muestras" }} gkey="mue">
            <SubLink item={{ to: "/muestras/tablero", label: "Tablero de productos", icon: Trello, testid: "nav-muestras-tablero" }} />
          </NavGroup>
        )}
        <NavLink item={{ to: "/instructivos", label: "Instructivos", icon: BookOpen, testid: "nav-instructivos" }} />
        {user?.es_admin && (
          <>
            <div className="px-3 pt-4 pb-1 text-[11px] uppercase tracking-wider text-slate-400 font-semibold">
              Administración
            </div>
            {ADMIN_NAV.map((i) => <NavLink key={i.to} item={i} />)}
          </>
        )}
      </nav>
      <div className="p-3 border-t border-slate-200 shrink-0">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center font-semibold text-slate-700 text-sm">
            {user?.nombre?.[0]?.toUpperCase() || "?"}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-800 truncate">{user?.nombre}</div>
            <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${colorRol(user?.role)}`}>
              {labelRol(user?.role)}
            </span>
          </div>
        </div>
        <button
          onClick={doLogout}
          data-testid="logout-button"
          className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors"
        >
          <LogOut size={16} /> Cerrar sesión
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex">
      {/* Sidebar fijo — solo en escritorio grande (lg+) */}
      <aside className={`hidden print:hidden lg:flex flex-col w-60 shrink-0 bg-white border-r border-slate-200 fixed inset-y-0 left-0 z-30 transition-transform duration-300 ${collapsed ? "-translate-x-full" : "translate-x-0"}`}>
        <SidebarInner showBell />
      </aside>

      {/* Botón flotante para expandir cuando la barra está colapsada (solo escritorio) */}
      {collapsed && (
        <button
          onClick={() => setCollapsed(false)}
          data-testid="sidebar-expand-btn"
          aria-label="Expandir menú"
          className="hidden print:hidden lg:flex fixed top-3 left-3 z-40 h-10 w-10 items-center justify-center rounded-xl bg-white border border-slate-200 shadow-md text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors"
        >
          <Menu size={20} />
        </button>
      )}

      {/* Barra superior con hamburguesa — móvil y tablet (<lg) */}
      <div className="lg:hidden print:hidden fixed top-0 inset-x-0 h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-40">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDrawerOpen(true)}
            data-testid="hamburger-button"
            aria-label="Abrir menú"
            className="p-2 -ml-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <Menu size={22} />
          </button>
          <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center text-white">
            <Footprints size={16} />
          </div>
          <span className="font-head font-bold">PLM</span>
        </div>
        <NotificationBell />
      </div>

      {/* Drawer lateral (móvil + tablet) */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col" data-testid="mobile-drawer">
          <SheetTitle className="sr-only">Menú de navegación</SheetTitle>
          <SidebarInner showBell={false} />
        </SheetContent>
      </Sheet>

      {/* Contenido */}
      <main className={`flex-1 pt-14 lg:pt-0 min-w-0 transition-[margin-left,padding-left] duration-300 print:!m-0 print:!p-0 ${collapsed ? "lg:ml-0 lg:pl-16" : "lg:ml-60 lg:pl-0"}`}>
        {children}
      </main>
    </div>
  );
}
